// src/Routes/AuthProtected.tsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useProfile } from "../Components/Hooks/UserHooks";
import { useUser } from "../hooks";
import LoadingOverlay from "../Components/LoadingOverlay";

const AuthProtected = (props: any) => {
  const dispatch: any = useDispatch();
  const { userProfile, loading, token } = useProfile();
  const { refreshUserContext, userObject, isContractor, isAuthenticated } = useUser();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Debug logging
  console.log('🔍 AuthProtected rendered for:', location.pathname + location.search);
  console.log('🔍 isAuthenticated:', isAuthenticated);
  console.log('🔍 loading:', loading);
  console.log('🔍 userObject:', userObject);
  console.log('🔍 currentUser from sessionStorage:', sessionStorage.getItem('authUser'));

  // Set a timeout for loading state to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.log('🔍 Loading timeout reached, proceeding with auth check');
        setLoadingTimeout(true);
      }
    }, 2000); // 2 second timeout

    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    if (userProfile && !userProfile.token) {
      // User profile exists but no token - might need to refresh
    }
  }, [userProfile, dispatch]);

  // Check authentication - be more explicit about the check
  const authUser = sessionStorage.getItem('authUser');
  const isUserAuthenticated = isAuthenticated || !!authUser;

  console.log('🔍 Final authentication check:', {
    isAuthenticated,
    authUserExists: !!authUser,
    finalResult: isUserAuthenticated,
    loadingTimeout
  });

  /*
    Navigate to login page with:
    1. Current location as 'from' for redirect after login
    2. Preserve any query parameters (including cid) in the redirect URL
  */
  if (!isUserAuthenticated) {
    console.log('🔍 User not authenticated, redirecting to login...');

    // Preserve the full URL including search params for redirect after login
    const redirectUrl = `${location.pathname}${location.search}`;

    // Check if there's a cid parameter that we need to preserve
    const urlParams = new URLSearchParams(location.search);
    const cid = urlParams.get('cid');

    // Build login URL with preserved cid if it exists
    let loginUrl = "/login";
    const loginParams = new URLSearchParams();

    if (cid) {
      loginParams.set('cid', cid);
      console.log('🔍 Preserving cid in login redirect:', cid);
    }

    if (loginParams.toString()) {
      loginUrl += `?${loginParams.toString()}`;
    }

    console.log('🔍 Redirecting to:', loginUrl);
    console.log('🔍 Will redirect back to:', redirectUrl);

    return (
      <Navigate
        to={loginUrl}
        state={{
          from: redirectUrl // This preserves the full original URL for redirect after login
        }}
        replace
      />
    );
  }

  console.log('🔍 User is authenticated, rendering protected content');

  // Show loading overlay on top of the protected content when loading (but not indefinitely)
  const shouldShowLoadingOverlay = loading && !loadingTimeout;

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Always render the protected content */}
      {props.children}
      
      {/* Show loading overlay on top when loading */}
      <LoadingOverlay
        isVisible={shouldShowLoadingOverlay}
        text="BidBro"
        subtext="Loading..."
        size="medium"
      />
    </div>
  );
};

export default AuthProtected;